<!--
  A projection assumes the outstanding principal never changes and no payment is ever
  recorded again - it is never true past the moment a real payment lands, so it must never
  read like a recorded fact (the brief's own words). The heading, the explanatory sentence
  and the "Projected for" caption carry that - not colour alone, so it still reads correctly
  to a screen reader and in greyscale. The backend has no amortisation schedule, so there is
  no real "remaining balance" curve to plot here - only this projected interest accrual
  series, honestly labelled, rather than a client-invented declining-balance line.
-->
<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import MoneyLineChart from '@/app/components/MoneyLineChart.vue'
import { formatDate, formatMoney } from '@taler/domain'
import type { LoanAccrualProjection } from '../internal/types.ts'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Card, Select } from '@taler/ui'

const props = defineProps<{ projections: readonly LoanAccrualProjection[]; months: number }>()
const emit = defineEmits<{ 'update:months': [months: number] }>()

const { locale, t } = useI18n()
const localeCode = computed(() => locale.value as 'en' | 'hu')

const HORIZONS = [3, 6, 12] as const
const horizonOptions = computed(() =>
  HORIZONS.map((value) => ({ value, label: t('loans.monthsAhead', { count: value }) })),
)

const points = computed(() =>
  props.projections.map((projection) => ({ date: projection.date, value: projection.interestAmount })),
)

const firstProjection = computed(() => props.projections[0])
const lastProjection = computed(() => props.projections.at(-1))

const summaryLabel = computed(() => {
  const first = firstProjection.value
  const last = lastProjection.value
  if (first === undefined || last === undefined) return t('loans.projectedInterest')
  return t('loans.projectionChartLabel', {
    from: formatDate(first.date, localeCode.value),
    to: formatDate(last.date, localeCode.value),
    toValue: formatMoney(last.interestAmount, { locale: localeCode.value }),
  })
})
</script>

<template>
  <Card
    role="region"
    :aria-label="t('loans.projectedInterest')"
    class="flex flex-col gap-[var(--space-cell)]"
  >
    <div class="flex flex-wrap items-center justify-between gap-[var(--space-cell)]">
      <div>
        <h3 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
          {{ t('loans.projectedInterest') }}
        </h3>
        <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('loans.projectionExplanation') }}
        </p>
      </div>
      <label class="flex items-center gap-[var(--space-hair)] text-[length:var(--text-step-1)]">
        <span class="text-[color:var(--color-text-muted)]">{{ t('loans.projectionHorizon') }}</span>
        <Select
          :model-value="months"
          :options="horizonOptions"
          @update:model-value="emit('update:months', $event)"
        />
      </label>
    </div>

    <p v-if="points.length === 0" class="text-[color:var(--color-text-muted)]">
      {{ t('loans.noProjections') }}
    </p>
    <template v-else>
      <MoneyLineChart :points="points" :label="summaryLabel" :locale="localeCode" area />
      <p v-if="firstProjection" class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
        {{ t('loans.projectedOn', { date: formatDate(firstProjection.date, localeCode) }) }}:
        <MoneyAmount :value="firstProjection.interestAmount" />
      </p>
    </template>
  </Card>
</template>
